import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Activity, Search, Eye, CheckCircle, XCircle, RefreshCw, Inbox, TrendingUp, Mail, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      
      const response = await axios.get(`${API}/emails/logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [filters.status]);

  const openDetailDialog = async (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/emails/logs/${log.id}`);
      setSelectedLog(response.data);
    } catch (error) {
      toast.error('Failed to load log details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        log.recipient_email.toLowerCase().includes(search) ||
        log.sender_email.toLowerCase().includes(search) ||
        log.template_name.toLowerCase().includes(search) ||
        log.sent_by_name.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const StatCard = ({ icon: Icon, label, value, subValue, color = "primary" }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subValue && <p className="text-xs text-muted-foreground mt-1">{subValue}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}/10`}>
            <Icon className={`w-5 h-5 text-${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="email-logs-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Email Performance</h1>
          <p className="text-muted-foreground mt-1">
            Monitor email delivery and performance metrics
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }} data-testid="refresh-logs-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Performance Stats */}
      {statsLoading ? (
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : stats && (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <StatCard
              icon={Mail}
              label="Total Emails Sent"
              value={stats.total_emails}
              subValue="All time"
            />
            <StatCard
              icon={CheckCircle}
              label="Delivered"
              value={stats.sent_emails}
              subValue={`${stats.success_rate}% success rate`}
              color="green-500"
            />
            <StatCard
              icon={XCircle}
              label="Failed"
              value={stats.failed_emails}
              subValue={stats.total_emails > 0 ? `${((stats.failed_emails / stats.total_emails) * 100).toFixed(1)}% failure rate` : '0%'}
              color="red-500"
            />
            <StatCard
              icon={TrendingUp}
              label="Success Rate"
              value={`${stats.success_rate}%`}
              subValue="Overall delivery rate"
            />
          </div>

          {/* Daily Stats Chart */}
          {stats.daily_stats && stats.daily_stats.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Last 7 Days Activity</CardTitle>
                <CardDescription>Daily email delivery performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2 h-32">
                  {stats.daily_stats.map((day, idx) => {
                    const total = day.sent + day.failed;
                    const maxTotal = Math.max(...stats.daily_stats.map(d => d.sent + d.failed), 1);
                    const height = total > 0 ? (total / maxTotal) * 100 : 5;
                    const sentHeight = total > 0 ? (day.sent / total) * height : 0;
                    const failedHeight = total > 0 ? (day.failed / total) * height : 0;
                    
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full flex flex-col-reverse" style={{ height: '100px' }}>
                          <div 
                            className="w-full bg-green-500 rounded-t-sm transition-all"
                            style={{ height: `${sentHeight}%` }}
                            title={`Sent: ${day.sent}`}
                          />
                          <div 
                            className="w-full bg-red-500 rounded-t-sm transition-all"
                            style={{ height: `${failedHeight}%` }}
                            title={`Failed: ${day.failed}`}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">{day.day}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-4 mt-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
                    <span>Delivered</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
                    <span>Failed</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Senders & Templates */}
          <div className="grid gap-4 md:grid-cols-2">
            {stats.emails_by_sender && stats.emails_by_sender.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Top Senders</CardTitle>
                  <CardDescription>Most used sender addresses</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.emails_by_sender.map((sender, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{sender.sender_name || sender._id}</p>
                          <p className="text-xs text-muted-foreground truncate">{sender._id}</p>
                        </div>
                        <Badge variant="secondary">{sender.count} emails</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {stats.emails_by_template && stats.emails_by_template.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Top Templates</CardTitle>
                  <CardDescription>Most used email templates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.emails_by_template.map((template, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <p className="text-sm font-medium">{template._id}</p>
                        <Badge variant="secondary">{template.count} emails</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email, template, or sender..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                  data-testid="search-logs-input"
                />
              </div>
            </div>
            <Select 
              value={filters.status || 'all'} 
              onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-status-select">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="sent">Delivered</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Recent Emails
          </CardTitle>
          <CardDescription>
            {filteredLogs.length} email{filteredLogs.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-64 w-full" />
          ) : filteredLogs.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <Inbox className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No emails found</h3>
              <p className="text-muted-foreground">
                {filters.search || filters.status 
                  ? 'Try adjusting your filters' 
                  : 'No emails have been sent yet'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Sender</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Sent By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map(log => (
                  <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                    <TableCell>
                      {log.status === 'sent' ? (
                        <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20 border-green-500/20">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Delivered
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="bg-red-500/10 text-red-600 hover:bg-red-500/20 border-red-500/20">
                          <XCircle className="w-3 h-3 mr-1" />
                          Failed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{log.recipient_email}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{log.sender_name}</p>
                        <p className="text-xs text-muted-foreground">{log.sender_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{log.template_name}</TableCell>
                    <TableCell>{log.sent_by_name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(log.sent_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDetailDialog(log)}
                        data-testid={`view-log-${log.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
            <DialogDescription>
              Complete information about this email
            </DialogDescription>
          </DialogHeader>
          {loadingDetail ? (
            <div className="space-y-4 mt-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : selectedLog && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">STATUS</p>
                  {selectedLog.status === 'sent' ? (
                    <Badge className="bg-green-500/10 text-green-600">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Delivered
                    </Badge>
                  ) : (
                    <Badge variant="destructive">
                      <XCircle className="w-3 h-3 mr-1" />
                      Failed
                    </Badge>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">SENT AT</p>
                  <p className="text-sm font-medium">{new Date(selectedLog.sent_at).toLocaleString()}</p>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">RECIPIENT</p>
                <p className="text-sm font-mono">{selectedLog.recipient_email}</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">FROM</p>
                <p className="text-sm font-medium">{selectedLog.sender_name} &lt;{selectedLog.sender_email}&gt;</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">TEMPLATE</p>
                <p className="text-sm font-medium">{selectedLog.template_name}</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">SENT BY</p>
                <p className="text-sm font-medium">{selectedLog.sent_by_name}</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">SUBJECT</p>
                <p className="text-sm font-semibold">{selectedLog.subject}</p>
              </div>

              {selectedLog.variables_used && Object.keys(selectedLog.variables_used).length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">VARIABLES USED</p>
                  <div className="p-2 bg-muted rounded font-mono text-xs">
                    {JSON.stringify(selectedLog.variables_used, null, 2)}
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">EMAIL BODY</p>
                <div 
                  className="p-4 bg-muted rounded-lg prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedLog.body }}
                />
              </div>

              {selectedLog.error_message && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">ERROR MESSAGE</p>
                  <div className="p-3 bg-red-500/10 text-red-600 rounded-lg text-sm flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {selectedLog.error_message}
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">LOG ID</p>
                <p className="text-xs font-mono text-muted-foreground">{selectedLog.id}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
